import React, { useMemo, useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { getCityChart } from '@/services/api/dashboard';

interface CityData {
    city: string;
    value: number;
    percentage: number;
    growthMoM?: number;
}

interface VehiclesByCityChartProps {
    onSeeAll?: () => void;
    data?: CityData[];
}

const VehiclesByCityChart: React.FC<VehiclesByCityChartProps> = ({ onSeeAll, data }) => {
    const [apiData, setApiData] = useState<CityData[]>([]);
    const [loading, setLoading] = useState(false);
    const initialized = React.useRef(false);

    // Fetch data from API on mount
    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;

        const fetchChartData = async () => {
            try {
                setLoading(true);
                const response = await getCityChart();
                const responseData = response.data || [];

                // Map API response to CityData format - take top 7
                const mappedData: CityData[] = responseData.slice(0, 7).map((item: any) => ({
                    city: item.cityName || 'Unknown',
                    value: Number(item.count) || 0,
                    percentage: Number(item.percentage) || 0,
                    growthMoM: item.growthMoM !== undefined ? Number(item.growthMoM) : undefined
                }));

                setApiData(mappedData);
            } catch (error) {
                console.error('Error fetching city chart data:', error);
                setApiData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchChartData();
    }, []);

    // Use provided data prop or API data
    const cityData: CityData[] = useMemo(() => data || apiData, [data, apiData]);

    const chartOptions: ApexOptions = useMemo(() => ({
        chart: {
            type: 'bar',
            height: 350,
            toolbar: {
                show: false,
            },
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800,
            },
            zoom: {
                enabled: false,
            },
            events: {
                mounted: function (chartContext, config) {
                    setTimeout(() => {
                        const gridLines = document.querySelectorAll('.apexcharts-gridlines-vertical line');
                        gridLines.forEach(line => {
                            line.setAttribute('stroke-dasharray', '5,5');
                        });
                    }, 10);
                }
            }
        },
        plotOptions: {
            bar: {
                borderRadius: 8,
                columnWidth: '80%',
                dataLabels: {
                    position: 'top',
                },
            }
        },
        dataLabels: {
            enabled: true,
            formatter: function (val: number, opts: any) {
                // Use percentage from API data
                const dataIndex = opts.dataPointIndex;
                const percentage = cityData[dataIndex]?.percentage || 0;
                return percentage.toFixed(0) + '%';
            },
            offsetY: 20, // Move down to be inside the bar
            style: {
                fontSize: '12px',
                fontWeight: 500,
                colors: ['#FFFFFF'],
                fontFamily: 'Plus Jakarta Sans, sans-serif',
            },
            background: {
                enabled: false,
            }
        },
        xaxis: {
            categories: cityData.map(d => d.city),
            tickPlacement: 'on', // Align ticks/lines with bars
            labels: {
                rotate: -45,
                rotateAlways: true,
                hideOverlappingLabels: false,
                style: {
                    colors: '#A3A3A3',
                    fontSize: '12px',
                    fontWeight: 500,
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                },
                offsetY: 4
            },
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
        },
        yaxis: {
            min: 0,
            max: (() => {
                // Calculate max dynamically with padding
                const maxValue = Math.max(...cityData.map(d => d.value));
                if (maxValue === 0) return 10; // Fallback

                const padding = maxValue * 0.2; // 20% padding for better visualization
                const maxWithPadding = maxValue + padding;

                // Smart rounding based on value size
                if (maxWithPadding <= 10) {
                    // Round to nearest even number for clean ticks
                    const rounded = Math.ceil(maxWithPadding);
                    return rounded % 2 === 0 ? rounded : rounded + 1;
                } else if (maxWithPadding <= 50) {
                    // Round to nearest 10
                    return Math.ceil(maxWithPadding / 10) * 10;
                } else if (maxWithPadding <= 100) {
                    // Round to nearest 20
                    return Math.ceil(maxWithPadding / 20) * 20;
                } else {
                    // Round to nearest 50
                    return Math.ceil(maxWithPadding / 50) * 50;
                }
            })(),
            tickAmount: 5,
            labels: {
                style: {
                    colors: '#A3A3A3',
                    fontSize: '12px',
                    fontWeight: 500,
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                },
                offsetX: -10,
                formatter: function (value) {
                    return Math.round(value).toString();
                },
            },
        },
        grid: {
            show: true,
            borderColor: '#E8E8E8',
            strokeDashArray: 0, // solid by default, vertical will be dashed via DOM
            padding: {
                top: 0,
                bottom: 50,
                left: 10,
                right: 10,
            },
            xaxis: {
                lines: {
                    show: true,
                },
            },
            yaxis: {
                lines: {
                    show: true,
                },
            },
        },
        annotations: {
            xaxis: [],
            yaxis: []
        },
        states: {},
        colors: ['#5E2AAC'],
        tooltip: {
            enabled: true,
            custom: function ({ series, seriesIndex, dataPointIndex }) {
                const value = series[seriesIndex][dataPointIndex];
                const cityInfo = cityData[dataPointIndex];
                const city = cityInfo.city;

                // Use growthMoM from API data
                const growthMoM = cityInfo.growthMoM !== undefined ? cityInfo.growthMoM : 0;
                const changeSign = growthMoM >= 0 ? '+' : '';
                const percentColor = growthMoM >= 0 ? '#1FC16B' : '#FB3748';

                return `
                    <div class="bg-white p-3 rounded-lg shadow-lg">
                        <div style="color: #A3A3A3; font-size: 12px; font-weight: 500; line-height: 16px;">${city}</div>
                        <div class="flex items-center gap-2 mt-1">
                            <span style="color: #171717; font-weight: 500; font-size: 18px; line-height: 24px; letter-spacing: -0.015em;">${value.toLocaleString()}</span>
                            <span style="font-size: 12px; font-weight: 500; line-height: 16px;">
                                <span style="color: ${percentColor};">${changeSign}${growthMoM.toFixed(1)}%</span>
                                <span style="color: #5C5C5C;"> MoM</span>
                            </span>
                        </div>
                    </div>
                `;
            },
        },
    }), [cityData]);

    const series = useMemo(() => [{
        name: 'Vehicles',
        data: cityData.map(d => d.value),
    }], [cityData]);

    return (
        <div className="bg-white rounded-2xl border border-gray-200 px-6 pt-5 pb-6 shadow-sm">
            {/* Header */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center">
                        <i className="ri-file-chart-line text-lg text-neutral-900" />
                    </div>
                    <h3 className="text-base font-medium text-gray-900" style={{ letterSpacing: '-0.011em', lineHeight: '24px' }}>
                        Vehicles by City
                    </h3>
                </div>

                <button
                    onClick={onSeeAll}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    See All
                </button>
            </div>

            {/* Chart */}
            <div className="mt-4 vehicles-by-city-chart-container">
                <style>
                    {`
                    .vehicles-by-city-chart-container .apexcharts-gridlines-vertical line {
                        stroke-dasharray: 5 !important;
                    }
                    .vehicles-by-city-chart-container .apexcharts-gridlines-horizontal line {
                        stroke-dasharray: 0 !important;
                    }
                `}
                </style>
                {loading ? (
                    <div className="flex items-center justify-center" style={{ height: '410px' }}>
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                    </div>
                ) : (
                    <Chart
                        options={chartOptions}
                        series={series}
                        type="bar"
                        height={410}
                    />
                )}
            </div>
        </div>
    );
};

export default VehiclesByCityChart;
